import { Button, chakra, Flex } from '@chakra-ui/react';
import { Reorder } from 'framer-motion';
import { useUpdateNodeInternals } from 'reactflow';
import { BlockOptionWithOutgoingMenu } from './BlockOptionOutgoingMenu';
import { BlockOption, NodeData } from '../flowEditorBlockNodeTypes';
import { AddIcon } from '@chakra-ui/icons';
import { Lightbulb } from 'lucide-react';
import { testIds as blockNodeTestIds } from '../FlowEditorBlockNode/testIds';

const OptionList = chakra(Reorder.Group);

type BlockOptionsType = {
  nodeData: NodeData;
  nodeId: string;
  isConnectable: boolean;
  removeOption: (optionId: string) => void;
  addOption: () => void;
  // Receives the options in their new order after a reorder. A drag fires this
  // once per item crossed; the history recorder coalesces a run of changes into
  // a single undo entry, so the whole drag is one Cmd+Z.
  onOptionReorder: (options: BlockOption[]) => void;
  /**
   * True while the parent node is being dragged across the canvas. The
   * option pills suppress their reorder layout animation during this so they
   * move rigidly with the node instead of springing to catch up (the
   * rubber-band effect).
   */
  dragging: boolean;
};

export function BlockOptions({
  nodeData,
  nodeId,
  isConnectable,
  removeOption,
  addOption,
  onOptionReorder,
  dragging,
}: BlockOptionsType) {
  const updateNodeInternals = useUpdateNodeInternals();

  if (!nodeData.options) {
    return null;
  }

  const options = nodeData.options;

  // Re-measure the node (deferred so it doesn't block the drop frame) so each
  // option's source handle follows its row after a reorder.
  const remeasureNode = () => {
    requestAnimationFrame(() => updateNodeInternals(nodeId));
  };

  const handleDragReorder = (reordered: BlockOption[]) => {
    onOptionReorder(reordered);
    remeasureNode();
  };

  // Move up / Move down from the option's menu: swap with the adjacent option,
  // the same explicit reorder the message bubbles offer.
  const moveOption = (optionId: string, direction: 'up' | 'down') => {
    const index = options.findIndex((o) => o.id === optionId);
    const target = direction === 'up' ? index - 1 : index + 1;
    const moved = options[index];
    const neighbour = options[target];
    if (index < 0 || target < 0 || target >= options.length || !moved || !neighbour) {
      return;
    }
    const reordered = [...options];
    reordered[index] = neighbour;
    reordered[target] = moved;
    onOptionReorder(reordered);
    remeasureNode();
  };

  return (
    <Flex
      flexDir="column"
      gap={2}
      borderRadius="md"
      // The same tint as the message panel above, so the editable options
      // read as part of the conversation rather than a separate tray.
      bgColor="bg.subtle"
      py={2}
      alignItems="flex-end"
      alignSelf="stretch"
    >
      <OptionList
        axis="y"
        values={nodeData.options}
        onReorder={handleDragReorder}
        display="flex"
        flexDirection="column"
        alignItems="flex-end"
        gap={2}
        width="100%"
        m={0}
        p={0}
        listStyleType="none"
      >
        {options.map((option, index) => (
          <BlockOptionWithOutgoingMenu
            key={option.id}
            nodeId={nodeId}
            option={option}
            removeOption={removeOption}
            isConnectable={isConnectable}
            canMoveUp={index > 0}
            canMoveDown={index < options.length - 1}
            onMoveUp={() => moveOption(option.id, 'up')}
            onMoveDown={() => moveOption(option.id, 'down')}
            dragging={dragging}
          />
        ))}
      </OptionList>
      <Button
        color="fg.default"
        fontWeight="normal"
        onClick={() => addOption()}
        mx={4}
        leftIcon={<AddIcon boxSize={3} />}
        size="sm"
        variant="ghost"
        data-testid={blockNodeTestIds.addOptionButton}
      >
        Add another option
      </Button>
      <Button
        color="fg.default"
        fontWeight="normal"
        mx={4}
        leftIcon={<Lightbulb size={16} />}
        size="sm"
        variant="ghost"
      >
        Offer a hint
      </Button>
    </Flex>
  );
}
